/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehicleStateInspectionProps {
  id: string;
  stateAbbrevation: string;
  inspectionDate: Date;
  vehicleId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class VehicleStateInspectionRecord extends Record<VehicleStateInspectionProps>({
  id: null as any,
  stateAbbrevation: null as any,
  inspectionDate: null as any,
  vehicleId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    stateAbbrevation: string;
    inspectionDate: string | number | Date;
    vehicleId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): VehicleStateInspectionRecord {
    if (json == null) {
      throw new Error('VehicleStateInspectionRecord.fromJS: parameter cannot be null');
    }
    return new VehicleStateInspectionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehicleStateInspectionJson.id',
      ),
      stateAbbrevation: getFromOrThrow(
        json,
        (j) => j.stateAbbrevation,
        'VehicleStateInspectionJson.stateAbbrevation',
      ),
      inspectionDate: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'inspectionDate', '0001-01-01T00:00:00.00Z'),
          'VehicleStateInspectionJson.inspectionDate',
        ),
      ),
      vehicleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
        'VehicleStateInspectionJson.vehicleId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'VehicleStateInspectionJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    stateAbbrevation: string;
    inspectionDate: string | number | Date;
    vehicleId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [VehicleStateInspectionRecord, Error[]] {
    if (json == null) {
      throw new Error('VehicleStateInspectionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehicleStateInspectionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehicleStateInspectionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      stateAbbrevation: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.stateAbbrevation,
            'VehicleStateInspectionJson.stateAbbrevation',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      inspectionDate: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'inspectionDate', '0001-01-01T00:00:00.00Z'),
              'VehicleStateInspectionJson.inspectionDate',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
            'VehicleStateInspectionJson.vehicleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'VehicleStateInspectionJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: VehicleStateInspectionProps) {
    super(props);
    if (props == null) {
      throw new Error('VehicleStateInspectionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehicleStateInspectionViewModelBase implements VehicleStateInspectionProps {
  protected readonly _props: VehicleStateInspectionProps;
  private _json?: VehicleStateInspectionProps;

  get id(): string {
    return this._props.id;
  }
  get stateAbbrevation(): string {
    return this._props.stateAbbrevation;
  }
  get inspectionDate(): Date {
    return this._props.inspectionDate;
  }
  get vehicleId(): string {
    return this._props.vehicleId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: VehicleStateInspectionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        stateAbbrevation: this._props.stateAbbrevation,
        inspectionDate: this._props.inspectionDate,
        vehicleId: this._props.vehicleId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehicleStateInspectionEntities = (state: AppState) =>
  state.entityStores.vehicleStateInspection.entities;

export const selectVehicleStateInspectionById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleStateInspection.entities.get(id);

export const selectVehicleStateInspectionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleStateInspection.meta.get(id);

export const selectVehicleStateInspectionAndMetadataById = (id: string) =>
  createSelector(
    selectVehicleStateInspectionById(id),
    selectVehicleStateInspectionMetadataById(id),
    (vehicleStateInspection, meta) => ({ vehicleStateInspection, meta }),
  );

export function selectVehicleStateInspectionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehicleStateInspectionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehicleStateInspectionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehicleStateInspectionEntitiesByStateAbbrevation(stateAbbrevation: string) {
  return createSelector(selectAllVehicleStateInspectionEntities, (entities) =>
    entities.filter((e) => e.stateAbbrevation === stateAbbrevation).valueSeq(),
  );
}

export function selectVehicleStateInspectionEntitiesByInspectionDate(inspectionDate: Date) {
  return createSelector(selectAllVehicleStateInspectionEntities, (entities) =>
    entities.filter((e) => e.inspectionDate === inspectionDate).valueSeq(),
  );
}

export function selectVehicleStateInspectionEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllVehicleStateInspectionEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectVehicleStateInspectionEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllVehicleStateInspectionEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
